<template>
    <div v-if="isSuperAdmin" class="col-12">
        <page-header :title="title" :items="items" />

        <awesome-table
            :labels="labels"
            :items="users"
            :pagination="pagination"
            :options="options"
            @rowClick="onRowClick"
            @paginationChange="onPaginationChange"
            @addButtonClick="openCreateEditUserModal"
        >
            <template #[`labels.actions`]="{ label }">
                <span style="color: gray">
                    {{ label }}
                </span>
            </template>
            <template #actions="{ item }">
                <a
                    href="#"
                    class="action-icon"
                    @click.stop.prevent="showDeleteModal(item.id)"
                >
                    <i class="far fa-trash-alt" />
                </a>
            </template>
        </awesome-table>

        <add-edit-user-modal
            :available-roles="ALL_ROLES"
            :selected-role="role.value"
            :logged-user="user"
            :updating-user="updatingUser"
            @stored="onUserStored"
            @updated="onUserUpdated"
            @hide="updatingUser = null"
        />

        <delete-modal
            :id="`confirm-delete-modal-${idToDelete}`"
            :is-open="isDeleteModalOpen"
            :title="`Are you sure you want to delete this user?`"
            @agree="removeUser"
            @disagree="onCloseDeleteModal"
        />
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import AddEditUserModal from '@/components/users/AddEditUserModal';
import DeleteModal from '@/components/elements/DeleteModal';

export default {
    page: {
        title: 'Users'
    },

    components: {
        AddEditUserModal,
        DeleteModal
    },

    data() {
        const {
            page: currentPage = 1,
            perPage = 10,
            sortBy = 'createdAt',
            order = 'DESC',
            q = null,
            role = null
        } = this.$route.query;

        return {
            title: 'Users',
            items: [
                {
                    text: 'Greener',
                    to: '/'
                },
                {
                    text: 'Users',
                    active: true
                }
            ],
            users: [],
            isDeleteModalOpen: false,
            labels: [
                { value: 'firstName', width: '12%' },
                {
                    value: 'lastName',
                    width: '12%'
                },
                { value: 'email', width: '27%' },
                {
                    text: 'Role',
                    value: 'roles[0].name',
                    sortable: false,
                    width: '10%'
                },
                {
                    value: 'createdAt',
                    width: '15%',
                    type: 'date'
                },
                {
                    value: 'updatedAt',
                    width: '15%',
                    type: 'date'
                },
                {
                    value: 'actions',
                    sortable: false,
                    width: '5%'
                }
            ],
            pagination: {
                currentPage: parseInt(currentPage),
                perPage: parseInt(perPage),
                total: 0,
                search: q || '',
                sortBy,
                descending: order.toLowerCase() === 'desc'
            },
            role: {
                value: role
            },
            updatingUser: null,
            idToDelete: null
        };
    },

    computed: {
        ...mapGetters({
            user: 'auth/loggedUser',
            isSuperAdmin: 'auth/isSuperAdmin',
            ALL_ROLES: 'roles/ALL_ROLES'
        }),

        roles() {
            return [{ value: null, text: 'All' }, ...this.ALL_ROLES];
        },

        options() {
            const options = {
                link: true,
                topScroll: true
            };

            if (this.role.value) {
                options.linkSuffix = `&role=${this.role.value}`;
            }

            return options;
        }
    },

    watch: {
        role() {
            this.pagination.currentPage = 1;
            this.pagination.sortBy = 'createdAt';
            this.fetchUsers();
        }
    },

    created() {
        this.fetchUsers();
    },

    methods: {
        ...mapActions({
            getUsers: 'users/index',
            deleteUser: 'users/destroy'
        }),

        async fetchUsers() {
            try {
                const response = await this.getUsers({
                    ...this.pagination,
                    role: this.role.value
                });

                this.users = response.rows;
                this.pagination.pages = Math.ceil(
                    response.count / this.pagination.perPage
                );
                this.pagination.total = response.count;
            } catch (err) {
                console.error(err);

                this.$bvToast.toast('You have no permissions to this view', {
                    title: 'Error!',
                    variant: 'danger',
                    solid: true
                });
            }
        },

        onPaginationChange(pagination) {
            this.pagination = {
                ...pagination
            };

            this.fetchUsers();
        },

        openCreateEditUserModal() {
            this.$bvModal.show('modalUserCreateEdit');
        },

        onRowClick(user) {
            const {
                createdAt,
                deletedAt,
                roleUser,
                updatedAt,
                roles: [role],
                ...data
            } = user;

            data.role = role.name;

            this.updatingUser = { ...data };
            this.openCreateEditUserModal();
        },

        onUserStored(user) {
            if (!user.roles || !user.roles.length) {
                return;
            }

            this.users = [user, ...this.users];
        },

        onUserUpdated(user) {
            const userIndex = this.users.findIndex(item => item.id === user.id);

            if (!~userIndex) {
                return;
            }

            this.users[userIndex] = user;
            this.users = [...this.users];
        },

        showDeleteModal(userId) {
            this.idToDelete = userId;
            this.isDeleteModalOpen = true;
        },

        async removeUser() {
            try {
                await this.deleteUser(this.idToDelete);

                this.users = this.users.filter(
                    user => user.id !== this.idToDelete
                );
                this.idToDelete = null;
                this.pagination.total--;

                this.$bvToast.toast('User has been deleted.', {
                    title: 'updated!',
                    variant: 'success',
                    solid: true
                });

                this.isDeleteModalOpen = false;
            } catch (error) {
                console.error(error);

                const message =
                    error.response &&
                    error.response.data &&
                    error.response.data.error
                        ? error.response.data.error
                        : 'Something went wrong!';

                this.$bvToast.toast(message, {
                    title: 'Error!',
                    variant: 'danger',
                    solid: true
                });
            }
        },
        onCloseDeleteModal() {
            this.idToDelete = null;

            this.isDeleteModalOpen = false;
        }
    }
};
</script>
